import React, { Component, useEffect, useRef, useState } from 'react';
import i18n from '../i18nextInit';
import { copyTextToClipboard } from '../Utils/clipboard_utils';
import './GuiErrorOutput.css';

function errorReport(error, source) {
  let details;
  if (error?.stack || error?.message) details = error.stack || error.message;
  else {
    try { details = JSON.stringify(error, null, 2) ?? String(error); }
    catch (_) { details = String(error); }
  }
  return `${new Date().toISOString()}\n${source}\n${navigator.userAgent}\n\n${details}`;
}

function ErrorDetails({ report, componentStack }) {
  const [copyStatus, setCopyStatus] = useState('');
  const heading = useRef();
  const details = useRef();
  useEffect(() => { heading.current?.focus(); }, []);
  const text = componentStack ? `${report}\n\nReact component stack:${componentStack}` : report;
  const copy = async () => {
    try {
      await copyTextToClipboard(text);
      setCopyStatus('copied_to_clipboard');
    } catch (_) {
      setCopyStatus('gui_error_copy_failed');
      details.current?.focus();
      details.current?.select();
    }
  };
  // Standalone HTML remains usable even if the app's theme/router providers fail.
  return <section className="gui-error-output" role="alertdialog" aria-labelledby="gui-error-title" aria-describedby="gui-error-help">
    <h1 id="gui-error-title" ref={heading} tabIndex={-1}>{i18n.t('gui_error_title')}</h1>
    <p id="gui-error-help">{i18n.t('gui_error_help')}</p>
    <textarea ref={details} readOnly value={text} spellCheck={false} aria-label={i18n.t('gui_error_details')} />
    <div className="gui-error-actions">
      <button type="button" onClick={copy}>{i18n.t('copy_to_clipboard')}</button>
      <button type="button" onClick={() => window.location.reload()}>{i18n.t('gui_error_reload')}</button>
    </div>
    <p role="status">{copyStatus ? i18n.t(copyStatus) : ''}</p>
  </section>;
}

class ErrorBoundary extends Component {
  state = { report: null, componentStack: '' };

  static getDerivedStateFromError(error) {
    return { report: errorReport(error, 'React error') };
  }

  componentDidCatch(_error, info) {
    this.setState({ componentStack: info.componentStack });
  }

  capture = (error, source) => {
    // Preserve the first failure rather than replacing it with teardown errors.
    this.setState((state) => state.report ? null : { report: errorReport(error, source) });
  };

  onError = (event) => {
    // Resource-load events have no runtime error message.
    if (event.message) this.capture(event.error ?? `${event.message}\n${event.filename}:${event.lineno}:${event.colno}`, 'Browser error');
  };

  onRejection = (event) => { this.capture(event.reason, 'Unhandled promise rejection'); };

  componentDidMount() {
    window.addEventListener('error', this.onError);
    window.addEventListener('unhandledrejection', this.onRejection);
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.onError);
    window.removeEventListener('unhandledrejection', this.onRejection);
  }

  render() {
    return this.state.report ? <ErrorDetails {...this.state} /> : this.props.children;
  }
}

export default function GuiErrorOutput({ children }) {
  // Temporary pilot diagnostics also apply to npm run build / server-served GUI.
  return process.env.REACT_APP_GUI_ERROR_OUTPUT === 'false' ? children : <ErrorBoundary>{children}</ErrorBoundary>;
}
